/**
 * Enhanced usePlaylistScheduler Hook
 *
 * Advanced React hook that provides intelligent playlist scheduling with:
 * - 8 schedule types (Always, TimeWindow, CrossDay, Prayer, Hijri, Gregorian, PrayerBased)
 * - Priority-based conflict resolution
 * - Performance optimization with caching
 * - Prayer time integration
 * - Real-time schedule evaluation
 *
 * Azure Best Practice: React hooks for state management and side effects
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Playlist,
  EnhancedSchedulerResult,
  SchedulerConfiguration,
  ensureAllSchedulesParsed,
} from "../types";
import { PrayerTimes } from "../types/PrayerTypes";
import { EnhancedPlaylistScheduler } from "../schedulers/EnhancedPlaylistScheduler";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate minutes until next prayer time
 * Returns null if no prayer times available
 */
function getMinutesUntilNextPrayer(
  now: Date,
  prayerTimes: PrayerTimes,
): number | null {
  const currentTimeStr = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const prayers = [
    prayerTimes.JamFajr,
    prayerTimes.JamDhuhr,
    prayerTimes.JamAsar,
    prayerTimes.JamMaghrib,
    prayerTimes.JamEsha,
  ].filter((time) => time && time !== "--:--");

  for (const prayerTimeStr of prayers) {
    if (prayerTimeStr > currentTimeStr) {
      const [prayerH, prayerM] = prayerTimeStr.split(":").map(Number);
      const [nowH, nowM] = currentTimeStr.split(":").map(Number);
      const nowMinutes = nowH * 60 + nowM;
      const prayerMinutes = prayerH * 60 + prayerM;
      return prayerMinutes - nowMinutes;
    }
  }

  return null; // No more prayers today
}

// ============================================================================
// HOOK INTERFACES
// ============================================================================

export interface UseEnhancedPlaylistSchedulerOptions {
  /** List of playlists to evaluate */
  playlists: Playlist[];

  /** Current prayer times (required for prayer-based schedules) */
  prayerTimes?: PrayerTimes;

  /** Screen data for prayer-specific configurations */
  screenData?: any; // ScreenData type - using any to avoid circular imports

  /** Scheduler configuration */
  configuration?: Partial<SchedulerConfiguration>;

  /** Enable advanced scheduling (feature flag for gradual rollout) */
  enableAdvancedScheduling?: boolean;

  /** Callback when schedule changes */
  onScheduleChange?: (result: EnhancedSchedulerResult) => void;

  /** Callback on evaluation error */
  onError?: (error: Error) => void;

  /** Evaluation interval in milliseconds (default: 10000) */
  evaluationInterval?: number;
}

export interface UseEnhancedPlaylistSchedulerResult {
  // Primary results
  /** Currently active playlist (highest priority) */
  currentPlaylist: Playlist | null;

  /** Loading state during initial evaluation */
  isLoading: boolean;

  /** Error message if evaluation fails */
  error: string | null;

  // Enhanced results
  /** Complete scheduler evaluation result */
  schedulerResult: EnhancedSchedulerResult | null;

  /** Number of active playlists */
  activePlaylistCount: number;

  /** Whether there are scheduling conflicts */
  hasConflicts: boolean;

  /** When the next schedule change will occur */
  nextScheduleChange: Date | null;

  // Control methods
  /** Force immediate re-evaluation */
  forceEvaluation: () => void;

  /** Clear scheduler cache */
  clearCache: () => void;

  /** Directly evaluate playlists (immediate) */
  evaluatePlaylists: () => void; // Add this line

  // Backward compatibility
  /** Whether current playlist is active */
  isActive: boolean;

  /** Reason for current schedule state */
  reason?: string;
}

// ============================================================================
// ENHANCED HOOK IMPLEMENTATION
// ============================================================================

export function useEnhancedPlaylistScheduler(
  options: UseEnhancedPlaylistSchedulerOptions,
): UseEnhancedPlaylistSchedulerResult {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [currentPlaylist, setCurrentPlaylist] = useState<Playlist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schedulerResult, setSchedulerResult] =
    useState<EnhancedSchedulerResult | null>(null);
  const [forceEvaluationCounter, setForceEvaluationCounter] = useState(0);

  // ============================================================================
  // REFS AND MEMOIZED VALUES
  // ============================================================================

  const schedulerRef = useRef<EnhancedPlaylistScheduler | null>(null);
  const evaluationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize scheduler instance (only once)
  const scheduler = useMemo(() => {
    if (!schedulerRef.current) {
      const config = {
        ...options.configuration,
        enableLogging: options.configuration?.enableLogging ?? false,
      };
      schedulerRef.current = new EnhancedPlaylistScheduler(config);
    }
    return schedulerRef.current;
  }, []); // Empty deps - only create once

  // Helper to check if logging is enabled
  const isLoggingEnabled = useMemo(
    () => scheduler.config?.enableLogging ?? false,
    [scheduler],
  );

  // ============================================================================
  // EVALUATION LOGIC
  // ============================================================================

  const evaluateSchedules = useCallback(() => {
    if (!options.playlists || options.playlists.length === 0) {
      setCurrentPlaylist(null);
      setSchedulerResult(null);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      // Use enhanced scheduler if enabled
      if (options.enableAdvancedScheduling !== false) {
        // Parse all playlists to ensure schedules are typed objects
        const parsedPlaylists = ensureAllSchedulesParsed(options.playlists);

        // Evaluate playlists
        const result = scheduler.evaluatePlaylists(
          parsedPlaylists,
          options.prayerTimes,
          new Date(),
          options.screenData, // Pass screenData to scheduler
        );

        // console.log(
        //   `🔍 [Scheduler] evaluated ${parsedPlaylists.length} playlists → ${result.activePlaylists.length} active | playlists: [${parsedPlaylists.map((p) => `"${p.name}"(id${p.id},pri${p.priority},st${p.scheduleType},parsed=${!!p.schedule})`).join(", ")}] | activeIds: [${result.activePlaylists.map((ap) => `"${ap.playlist?.name}"(effPri${ap.effectivePriority})`).join(", ")}] | statuses: [${result.allStatuses.map((s) => `"${s.playlistName}":${s.isActive}(${s.reason?.substring(0, 40)})`).join(", ")}]`,
        // );

        // Update state
        setSchedulerResult(result);

        // Set current playlist (highest priority active playlist)
        const topPlaylist =
          result.activePlaylists.length > 0
            ? result.activePlaylists[0].playlist
            : null;
        setCurrentPlaylist(topPlaylist);

        // Notify listeners
        if (options.onScheduleChange) {
          options.onScheduleChange(result);
        }

        // Schedule next evaluation
        scheduleNextEvaluation(result.nextEvaluationTime);
      } else {
        // Advanced scheduling disabled - no active playlist
        setCurrentPlaylist(null);
        setSchedulerResult(null);
      }
    } catch (evaluationError) {
      const errorMessage =
        evaluationError instanceof Error
          ? evaluationError.message
          : "Unknown scheduler error";

      setError(errorMessage);

      if (options.onError) {
        options.onError(
          evaluationError instanceof Error
            ? evaluationError
            : new Error(errorMessage),
        );
      }

      // Retry evaluation after error
      scheduleNextEvaluation();
    } finally {
      setIsLoading(false);
    }
  }, [
    options.playlists,
    options.prayerTimes,
    options.enableAdvancedScheduling,
    options.onScheduleChange,
    options.onError,
    scheduler,
    isLoggingEnabled,
  ]);

  // ============================================================================
  // SCHEDULING AND TIMING
  // ============================================================================

  const scheduleNextEvaluation = useCallback(
    (nextEvaluationTime?: Date) => {
      // Clear existing timer
      if (evaluationTimerRef.current) {
        clearTimeout(evaluationTimerRef.current);
      }

      // Calculate next evaluation time with smart interval based on prayer times
      const now = new Date();
      const defaultInterval = options.evaluationInterval ?? 10000; // 10 seconds default (reduced from 30s)

      let delay: number;

      if (nextEvaluationTime) {
        // Use suggested next evaluation time from scheduler
        delay = Math.max(1000, nextEvaluationTime.getTime() - now.getTime());
      } else {
        // Use default interval
        delay = defaultInterval;
      }

      // Smart interval adjustment based on proximity to prayer times
      if (options.prayerTimes) {
        const minutesUntilNextPrayer = getMinutesUntilNextPrayer(
          now,
          options.prayerTimes,
        );

        if (minutesUntilNextPrayer !== null) {
          // Within 2 minutes of prayer: evaluate every 1 second
          if (minutesUntilNextPrayer <= 2) {
            delay = Math.min(delay, 1000);
            if (isLoggingEnabled) {
            }
          }
          // Within 5 minutes of prayer: evaluate every 10 seconds
          else if (minutesUntilNextPrayer <= 5) {
            delay = Math.min(delay, 10000);
          }
          // Within 15 minutes of prayer: evaluate every 15 seconds
          else if (minutesUntilNextPrayer <= 15) {
            delay = Math.min(delay, 15000);
          }
        }
      }

      // Cap maximum delay to prevent hanging
      delay = Math.min(delay, 30000); // Max 30 seconds (reduced from 60s)

      // Schedule next evaluation
      evaluationTimerRef.current = setTimeout(() => {
        evaluateSchedules();
      }, delay);
    },
    [
      evaluateSchedules,
      options.evaluationInterval,
      options.prayerTimes,
      isLoggingEnabled,
    ],
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initial evaluation and when dependencies change
  useEffect(() => {
    evaluateSchedules();

    // Cleanup timer on unmount
    return () => {
      if (evaluationTimerRef.current) {
        clearTimeout(evaluationTimerRef.current);
      }
    };
  }, [
    evaluateSchedules,
    forceEvaluationCounter,
    options.playlists,
    options.prayerTimes,
    isLoggingEnabled,
  ]);

  // Clear cache when prayer times change (important for prayer-based schedules)
  useEffect(() => {
    if (options.prayerTimes) {
      scheduler.clearCache();
    }
  }, [options.prayerTimes, scheduler, isLoggingEnabled]);

  // ============================================================================
  // CONTROL METHODS
  // ============================================================================

  const forceEvaluation = useCallback(() => {
    setForceEvaluationCounter((prev) => prev + 1);
  }, []);

  const clearCache = useCallback(() => {
    scheduler.clearCache();
    forceEvaluation();
  }, [scheduler, forceEvaluation]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const activePlaylistCount = schedulerResult?.activePlaylists.length ?? 0;
  const hasConflicts = (schedulerResult?.conflictsResolved.length ?? 0) > 0;
  const nextScheduleChange = schedulerResult?.nextEvaluationTime ?? null;

  // Backward compatibility values
  const isActive = currentPlaylist !== null;
  const reason = schedulerResult?.allStatuses.find(
    (s) => s.playlistId === currentPlaylist?.id,
  )?.reason;

  // ============================================================================
  // RETURN HOOK RESULT
  // ============================================================================

  return {
    // Primary results
    currentPlaylist,
    isLoading,
    error,

    // Enhanced results
    schedulerResult,
    activePlaylistCount,
    hasConflicts,
    nextScheduleChange,

    // Control methods
    forceEvaluation,
    clearCache,
    evaluatePlaylists: evaluateSchedules, // Add this line to expose the function

    // Backward compatibility
    isActive,
    reason,
  };
}
